import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "components/fields/InputField";
import Turnstile from "react-turnstile";
import axios from "axios";
import toast from "react-hot-toast";
import { API_URL } from "utils/config";
import { useDispatch } from "react-redux";
import { forgotPassword, verifyEmail } from "../../redux/action/index";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(true);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false); // New loading state
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    // Optionally handle logic if needed on mount
  }, []);

  const validateEmail = async (email) => {
    try {
      dispatch(verifyEmail(email)).then((response) => {
        if (response !== undefined) {
          setEmailVerified(true);
          setErrors({});
          toast.success("Email is verified.");
        } else {
          setEmailVerified(false);
          setErrors({ email: "Error verifying email." });
        }
      });
    } catch (error) {
      setEmailVerified(false);
      setErrors({ email: "Error verifying email." });
      console.error("Error:", error);
    }
  };

  const handleEmailChange = (e) => {
    const { value } = e.target;
    setEmail(value);

    // Validate form
    if (value.trim()) {
      validateEmail(value);
    }
  };

  const handleCaptchaChange = async (value) => {
    try {
      const body = {
        captcha: true,
        g_recaptcha_response: value,
      };
      const response = await axios.post(API_URL + "/auth/verify_captcha", body);
      if (response?.data?.status === 200) {
        setCaptchaVerified(true);
        console.log("Captcha is verified");
      } else {
        toast.error(response?.data?.detail[0]?.msg);
        setCaptchaVerified(false);
        console.log("Captcha is not verified");
      }
    } catch (error) {
      console.error("Captcha verification error:", error);
      setCaptchaVerified(false);
    }
  };

  const handleSubmit = async () => {
    if (emailVerified && captchaVerified) {
      setLoading(true); // Set loading to true when API call starts
      try {
        const response = await dispatch(forgotPassword({ email: email }));
        console.log(response);
        navigate("/auth/sign-in");
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false); // Reset loading when the API call completes
      }
    } else {
      toast.error("Please verify your email and captcha.");
    }
  };

  return (
    <div className="mb-16 mt-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      {/* Sign in section */}
      <div className="mt-[4vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[440px]">
        <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
          Forgot Password
        </h4>
        <p className="mb-9 ml-1 text-base text-gray-600">
          Enter the email associated with your account and we'll send you a link
          to reset your password!
        </p>

        {/* Email Field */}
        <div className="relative mb-3 flex w-full items-center">
          <InputField
            variant="auth"
            extra="mr-2 w-full"
            label="Email*"
            placeholder="mail@gmail.com"
            id="email"
            value={email}
            onChange={handleEmailChange}
            type="text"
            error={errors.email}
          />
          <div className="absolute right-4 top-10">
            {!emailVerified && errors.email ? (
              <span className="text-sm text-green-500">❌</span>
            ) : emailVerified ? (
              <span className="text-sm text-red-500">✔️</span>
            ) : null}
          </div>
        </div>
        {errors.email && (
          <p className="mb-3 text-sm text-red-500">{errors.email}</p>
        )}

        {/* Captcha */}
        <Turnstile
          sitekey="0x4AAAAAAAkH86FYslmjxCH3"
          className="mt-2"
          onVerify={(token) => handleCaptchaChange(token)}
        />

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || !emailVerified || !captchaVerified} // Disable if loading
          className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-600 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
        >
          {loading ? "Loading..." : "Submit"} {/* Change button text */}
        </button>

        <div className="mt-4">
          <span className="text-sm font-medium text-navy-700 dark:text-gray-600">
            Not registered yet?
          </span>
          <a
            href="/auth/register"
            className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
          >
            Create an account
          </a>
        </div>
      </div>
    </div>
  );
}
